import { mbPlatformStyle } from '@mightybyte/rnw.utils.style-utils';
import React from 'react';
import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { HiveUserType, useHiveUsersQuery } from '../../screens/MyHiveData/useHiveUsersQuery.gql';
import ChevronDown from 'react-native-vector-icons/FontAwesome';
import { GAME_TYPES_TYPE } from '../../../constants/constants';

type TOnPressParams = {
  userId: string;
  allowedHiveGames?: GAME_TYPES_TYPE[] | null;
};

type THiveSettingsUserTableProps = {
  hiveId: string;
  onPress: (params: TOnPressParams) => void;
  users: HiveUserType[];
  allowedClassHiveGames?: GAME_TYPES_TYPE[] | null;
};

export default function HiveSettingsUserTable(props: THiveSettingsUserTableProps) {
  const { hiveId, onPress, users, allowedClassHiveGames } = props;
  const { width } = useWindowDimensions();
  const sizeLimit = width < 800;

  return (
    <>
      <View style={[styles.container, sizeLimit && { paddingHorizontal: 2 }]}>
        <ScrollView style={[styles.tableContainer, sizeLimit && { padding: 10 }]}>
          <View style={styles.header}>
            {['Last Name', 'First Name', 'User Name', 'Password', 'Boards Available'].map(
              column => (
                <View style={styles.headerView} key={column}>
                  <Text style={[styles.headerText, sizeLimit && { fontSize: 10 }]}>{column}</Text>
                </View>
              ),
            )}

            <Text />
          </View>

          {users?.map((user, index: number) => {
            return (
              <View style={[styles.row, index === 0 ? { borderTopWidth: 0 } : null]}>
                <View style={styles.rowView}>
                  <Text style={[styles.rowText, sizeLimit && styles.rowTextSmall]}>
                    {user.lastName}
                  </Text>
                </View>
                <View style={styles.rowView}>
                  <Text style={[styles.rowText, sizeLimit && styles.rowTextSmall]}>
                    {user.firstName}
                  </Text>
                </View>
                <View style={styles.rowView}>
                  <Text style={[styles.rowText, sizeLimit && styles.rowTextSmall]}>
                    {user.userName}
                  </Text>
                </View>
                <View style={styles.rowView}>
                  <Text style={[styles.rowText, sizeLimit && styles.rowTextSmall]}>
                    {user.password}
                  </Text>
                </View>
                <View style={styles.rowView}>
                  <TouchableOpacity
                    onPress={() => {
                      // Case 1: both are null → keep null (inherit default)
                      if (allowedClassHiveGames == null && user.allowedHiveGames == null) {
                        onPress({
                          userId: user.id,
                          allowedHiveGames: null,
                        });
                        return;
                      }

                      // Case 2: if either is an empty array, meaning "all boards"
                      if (
                        Array.isArray(allowedClassHiveGames) &&
                        allowedClassHiveGames.length === 0 &&
                        Array.isArray(user.allowedHiveGames) &&
                        user.allowedHiveGames.length === 0
                      ) {
                        onPress({
                          userId: user.id,
                          allowedHiveGames: [],
                        });
                        return;
                      }

                      if (user.allowedHiveGames) {
                        onPress({
                          userId: user.id,
                          allowedHiveGames: user.allowedHiveGames as GAME_TYPES_TYPE[],
                        });
                        return;
                      }

                      // Case 3: Merge normally if not null/empty
                      const mergedAllowedGames = Array.from(
                        new Set([
                          ...(allowedClassHiveGames || []),
                          ...(user.allowedHiveGames || []),
                        ]),
                      ) as GAME_TYPES_TYPE[];

                      onPress({
                        userId: user.id,
                        allowedHiveGames: mergedAllowedGames,
                      });
                    }}
                    style={[styles.buttonDropdown, sizeLimit && { width: 80, height: 30 }]}
                  >
                    <Text style={[styles.textDropdown, sizeLimit && { fontSize: 9 }]}>
                      {user.allowedHiveGames?.length !== 0 &&
                      ((allowedClassHiveGames?.length && user.allowedHiveGames?.length) ||
                        (allowedClassHiveGames?.length && user.allowedHiveGames === null) ||
                        (allowedClassHiveGames?.length && user.allowedHiveGames?.length === 0) ||
                        user.allowedHiveGames?.length)
                        ? 'Limited'
                        : 'All Boards'}
                    </Text>
                    <ChevronDown
                      name="chevron-down"
                      size={sizeLimit ? 10 : 14}
                      style={styles.dropdownArrow}
                    />
                  </TouchableOpacity>
                </View>
              </View>
            );
          })}
        </ScrollView>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: 900,
    paddingHorizontal: 54,
  },
  tableContainer: {
    width: '100%',
    borderRadius: 16,
    backgroundColor: '#FFF5DD',
    padding: 20,
    borderColor: '#FFCF53',
    borderTopWidth: 2,
    borderBottomWidth: 6,
    borderRightWidth: 3,
    borderLeftWidth: 3,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    backgroundColor: '#FEE9B2',
    borderRadius: 12,
  },
  headerText: {
    fontFamily: 'Secular One',
    fontWeight: '700',
    fontSize: 15,
    alignSelf: 'flex-start',
    color: '#333333',
  },
  headerView: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerViewLast: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    ...mbPlatformStyle({
      web: {
        paddingHorizontal: 54,
      },
    }),
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    borderTopColor: 'black',
    borderTopWidth: 1,
    backgroundColor: '#FFF5DD',
  },
  rowText: {
    fontFamily: 'Inter',
    fontWeight: '600',
    fontSize: 14,
    alignSelf: 'flex-start',
    color: '#453B3B',
  },
  rowTextSmall: {
    fontSize: 11,
  },
  rowView: {
    flex: 1,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  buttonDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 36,
    width: 161,
    paddingHorizontal: 10,
    position: 'relative',
    borderRadius: 45,
    backgroundColor: '#FEE9B2',
  },
  textDropdown: {
    fontFamily: 'Secular One',
    fontSize: 16,
    fontWeight: '400',
    color: '#E87030',
  },
  dropdownArrow: {
    marginLeft: 5,
    color: '#E87030',
  },
});
